import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import styled, { css } from 'styled-components';
import { ReactComponent as BaseDetailsIcon } from '@ubeya/shared-web/assets/calendar.svg';
import { ReactComponent as BaseDocumentFileIcon } from '@ubeya/shared-web/assets/order.svg';
import { ReactComponent as BaseExpensesIcon } from '@ubeya/shared-web/assets/payroll-dollar.svg';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import {
  ExplanationTooltip,
  Flex,
  header3SemiBold,
  InputField as BaseInputField,
  SelectField as BaseSelectField,
  DescriptionFieldForm,
  Table as BaseTable,
  FlexColumn,
  Button as BaseButton
} from '@ubeya/shared-web/components';
import { required } from '@ubeya/shared/utils/form';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import {
  CONSTELATION_ACCOUNT_ID,
  CONSTELATION_NL_ACCOUNT_ID,
  CONSTELLATION_IRELAND_ACCOUNT_ID
} from '@ubeya/shared/utils/accounts';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import moment from 'moment';
import { groupWages, wageCellFormatter } from 'shared/utils/wages';
import PositionComplianceTable from './Compliance/PositionComplianceTable';
import PositionWithDrawerRenderer from './PositionWithDrawerRenderer';
import PositionWageModal from './PositionWageModal';
import PositionWageActionRenderer from './PositionWageActionRenderer';
import { DEFAULT_POSITION_RATE_EFFECTIVE_DATE, JOB_DESCRIPTION_MAX_LENGTH } from 'settings/constants';

const Container = styled(Flex)`
  flex-direction: column;
`;

const Action = styled.div`
  flex: 1;
`;

const Button = styled(BaseButton)`
  margin: 20px 0;
`;

const Table = styled(BaseTable)`
  overflow-x: auto;

  .row.past {
    background: ${({ theme }) => theme.colors.grey1};
    color: ${({ theme }) => theme.colors.grey3};
  }

  .row.future {
    color: ${({ theme }) => theme.colors.grey3};
  }
`;

const TableWrapper = styled(FlexColumn)`
  padding: 10px 40px;
`;

const icon = css`
  margin-right: 10px;
`;

const DocumentFileIcon = styled(BaseDocumentFileIcon)`
  ${icon};
  g {
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const DetailsIcon = styled(BaseDetailsIcon)`
  ${icon};

  height: 18px;
  width: 18px;
  g {
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const ExpensesIcon = styled(BaseExpensesIcon)`
  ${icon};
  g {
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const SelectField = styled(BaseSelectField)`
  .select-arrow {
    display: none;
  }

  .select-input {
    input {
      &::placeholder {
        color: 'red';
      }
    }
  }
`;

const MainLabel = styled.div`
  display: flex;
  align-items: center;
  ${header3SemiBold};
  font-size: 16px;
  background: ${({ theme }) => theme.colors.grey1};
  color: ${({ theme }) => theme.colors.black};
  margin: 20px 20px 0 20px;
  line-height: 1.78;
  height: 40px;
  padding: 3px 36px 5px 16px;
`;

const FieldsArea = styled.div`
  padding: 10px 40px;
  display: flex;
  justify-content: space-between;
`;

const ComplianceArea = styled.div`
  padding: 10px 40px;
`;

const InputField = styled(BaseInputField)`
  width: ${({ $width }) => $width || 150}px;
`;

const NameRow = styled(FieldsArea)`
  justify-content: flex-start;
  align-items: flex-end;
`;

const PaycodeInputField = styled(InputField)`
  margin-left: 16px;
`;

const DescriptionFieldFormWrapper = styled.div`
  max-width: 100%;
  margin: 10px 40px;
  z-index: 1;

  .description-field-label {
    font-size: 13px;
  }
`;

const PositionModalFields = ({
  mappedFields,
  options,
  isFlatWage,
  setAddedPosition,
  setComplianceModal,
  selectedPosition
}) => {
  const { t, isAccountNetworkAccessSupplier } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { isUbeyaAdmin } = useUser();

  const {
    input: { onChange: onChangePositionName }
  } = useField('id');

  const {
    input: { value }
  } = useField('branchIds');

  const selectedBranchesCount = Array.isArray(value) ? value.length : 0;

  const myPrivileges = useContext(PrivilegesContext);
  const { hasClients } = useCRM();
  const { branchesOptions } = useBranches();

  const onChange = useCallback(
    (newId) => {
      if (!options.find(({ value: optValue }) => optValue === newId)) {
        setAddedPosition((prev) => [...prev, { value: newId, label: newId }]);
      }
      onChangePositionName(newId);
    },
    [onChangePositionName, options, setAddedPosition]
  );

  const onBlur = useCallback(
    (e) => {
      if (!e.target.value) {
        return;
      }
      return onChange(e.target.value);
    },
    [onChange]
  );

  const shouldDisableCreatedByClient = isUbeyaAdmin
    ? false
    : !!selectedPosition?.createdByClientId &&
      ![CONSTELATION_ACCOUNT_ID, CONSTELATION_NL_ACCOUNT_ID, CONSTELLATION_IRELAND_ACCOUNT_ID].includes(accountId);

  const disableNameEdit = selectedPosition?.isGeneric || (isUbeyaAdmin ? false : shouldDisableCreatedByClient);

  // WAGES
  const hasAmendPayRatePrivilege = isUbeyaAdmin || !!myPrivileges[privileges.AMEND_PAY_RATE];
  const disableAllWagesActions = !hasAmendPayRatePrivilege || shouldDisableCreatedByClient;

  const showBillRate = !!myPrivileges[privileges.CRM] && hasClients;

  return (
    <Container>
      <MainLabel>
        <DetailsIcon />
        {t('positionDetails')}
      </MainLabel>
      <NameRow>
        {selectedPosition?.id ? (
          <InputField
            name="slug"
            title={t('name')}
            isDisabled={disableNameEdit}
            validate={required}
            required={!isAccountNetworkAccessSupplier}
            $width={300}
          />
        ) : (
          <SelectField
            name="id"
            title={t('name')}
            placeholder={t('positionName')}
            options={options}
            onBlur={onBlur}
            onChange={onChange}
            isDisabled={selectedPosition?.id}
            validate={required}
            required
            isMulti={false}
            usePortal
            creatable
          />
        )}

        {isUbeyaAdmin && <PaycodeInputField name="paycode" title="Pay code (for super admins only)" $width={180} />}
      </NameRow>
      <DescriptionFieldFormWrapper>
        <DescriptionFieldForm
          name="jobDescription"
          label={t('jobDescription')}
          placeholder={t('jobDescriptionPlaceHolder')}
          autoFocus={false}
          maxLength={JOB_DESCRIPTION_MAX_LENGTH}
        />
      </DescriptionFieldFormWrapper>

      {!!myPrivileges[privileges.ACCOUNT_ADMIN] && (
        <FieldsArea>
          <BaseSelectField
            name="branchIds"
            title={t('branches')}
            options={branchesOptions}
            placeholder={selectedBranchesCount === 0 ? t('allBranches') : undefined}
            isMulti
            usePortal
            clearable
            controlShouldRenderValue={false}
          />
        </FieldsArea>
      )}

      <NewWagesComponent
        isFlatWage={isFlatWage}
        selectedPosition={selectedPosition}
        hasAmendPayRatePrivilege={hasAmendPayRatePrivilege}
        showBillRate={showBillRate}
        disableAllWagesActions={disableAllWagesActions}
      />

      {myPrivileges[privileges.COMPLIANCE] && (
        <>
          <MainLabel>
            <DocumentFileIcon />
            <ExplanationTooltip title={t('compliance')} body={t('tooltipCompliance')} />
          </MainLabel>
          <ComplianceArea>
            <PositionComplianceTable t={t} mappedFields={mappedFields} setComplianceModal={setComplianceModal} />
          </ComplianceArea>
        </>
      )}
    </Container>
  );
};

export default PositionModalFields;

const NewWagesComponent = ({ isFlatWage, selectedPosition, showBillRate, disableAllWagesActions }) => {
  const [editedWage, setEditedWage] = useState();
  const { t, currency, dateFormat } = useContext(ConfigContext);
  const { mappedPositions } = usePositions();
  const formattingMap = useFormattingMap();

  const { data = [], handleDelete, handleAdd, handleEdit } = useArrayManagement(`wages`);

  const handleAddWage = useCallback((item) => {
    setEditedWage(item);
  }, []);

  const handleEditWage = useCallback((item) => {
    setEditedWage(item);
  }, []);

  const handleRowClick = useCallback(
    ({ rowData, rowData: { id, positionId } }) => (!id ? handleAddWage({ positionId }) : handleEditWage(rowData)),
    [handleAddWage, handleEditWage]
  );

  const wageFormatter = useCallback(
    ({ value, rowData, key, currency: withCurrency = true }) => {
      return wageCellFormatter({
        value,
        rowData,
        t,
        key,
        currency: withCurrency,
        isFlatWage,
        formattingMap,
        mappedPositions,
        showDefaultWages: false,
        includeZero: true
      });
    },
    [formattingMap, isFlatWage, mappedPositions, t]
  );

  const columns = useMemo(
    () => [
      {
        Header: '',
        dataKey: 'id',
        Cell: PositionWithDrawerRenderer,
        width: 150,
        captureRowClick: false
      },
      {
        Header: t('positionModalHourlyRate'),
        dataKey: 'wage.hourlyWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'hourlyWage' }),
        width: 80
      },
      isFlatWage && {
        Header: t('flatWage'),
        dataKey: 'wage.flatWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatWage' }),
        width: 80
      },
      isFlatWage && {
        Header: t('flatHours'),
        dataKey: 'wage.flatHours',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatHours', currency: false }),
        width: 80
      },
      {
        Header: t('positionModalBillRate'),
        dataKey: 'clientWage.hourlyWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'hourlyWage' }),
        width: 80
      },
      {
        Header: t('effectiveFrom'),
        dataKey: 'effectiveDate',
        formatter: (value) => {
          if (!value || value === DEFAULT_POSITION_RATE_EFFECTIVE_DATE) {
            return '-';
          }
          return moment(value).format(dateFormat);
        }
      }
    ],
    [dateFormat, isFlatWage, t, wageFormatter]
  );

  const defaultExtendsRows = data.length <= 2 ? { 0: true } : { 0: false };

  const groupedWages = groupWages({
    positions: [selectedPosition?.id],
    data: data.map((current) => ({ ...current, positionId: selectedPosition?.id }))
  });

  const showDefaultPositionEffectiveDate = data.length === 0;

  const validate = useCallback(
    ({ effectiveDate = '' }) => {
      const errors = {};
      const wagesEffectiveDates = new Set(
        data
          .filter((wage) => wage.id !== editedWage?.id)
          .map((wage) => moment(wage.effectiveDate).format(API_DATE_FORMAT))
      );
      if (effectiveDate && wagesEffectiveDates.has(moment(effectiveDate).format(API_DATE_FORMAT))) {
        errors.effectiveDate = t('overlappingDateEffectiveFromForm');
      }
      return errors;
    },
    [data, editedWage?.id, t]
  );

  const handleAddFirstPositionWage = () => {
    handleAddWage({ positionId: undefined });
  };

  return (
    <>
      <MainLabel>
        <ExpensesIcon />
        {t('positionModalHourlyRates')} ({currency})
      </MainLabel>

      <TableWrapper>
        {data.length === 0 ? (
          disableAllWagesActions ? (
            <div>{t('noWages')}</div>
          ) : (
            <Action>
              <Button type="button" secondary narrow onClick={handleAddFirstPositionWage}>
                {` + ${t('addPositionWage')} `}
              </Button>
            </Action>
          )
        ) : (
          <Table
            autoHeight
            gridHeight={400}
            headerRowHeight={50}
            columns={columns}
            defaultExtendsRows={defaultExtendsRows}
            data={groupedWages}
            rowStyle={({ wageStatus }) => wageStatus}
            onRowClick={!disableAllWagesActions ? handleRowClick : null}
            actionRenderer={({ rowData }) =>
              !disableAllWagesActions && (
                <PositionWageActionRenderer
                  rowData={rowData}
                  columnData={{
                    handleAdd: handleAddWage,
                    handleDelete,
                    handleEdit: handleEditWage
                  }}
                />
              )
            }
            shadow={false}
          />
        )}
      </TableWrapper>

      {editedWage && (
        <PositionWageModal
          disableAllWagesActions={disableAllWagesActions}
          showDefaultPositionEffectiveDate={showDefaultPositionEffectiveDate}
          showBillRate={showBillRate}
          validate={validate}
          onClose={() => setEditedWage()}
          onConfirm={(item) => {
            if (item.id) {
              handleEdit(item.id, item);
            } else {
              handleAdd(item);
            }
            setEditedWage();
          }}
          item={editedWage}
        />
      )}
    </>
  );
};
